import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { bounceAnimation, rippleAnimation } from '@core/styles/animations'

import type { BaseButtonProps } from './types'
import { borderRadiusSettings, fontSettings, heightSettings } from './types'

const rippleRings = css`
  opacity: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  content: '';
  height: 100%;
  width: 100%;
  background: var(--primary-button-background, var(--gradient));
  animation: var(
    --primary-button-animation,
    ${rippleAnimation.name} 2s cubic-bezier(0.65, 0, 0.34, 1) 0s infinite
  );
  z-index: -1;
`

const bounceAnimationSetting = css`
  animation-name: ${bounceAnimation};
  animation-duration: 0.3s;
  animation-iteration-count: 1;
`

const primaryButtonStyle = css`
  background: var(--primary-button-background, var(--gradient));
  border: var(--primary-button-border, none);
  font-family: var(--primary-button-font-family, inherit);
  color: var(--primary-button-color, white) !important;

  // Need a higher specificity to override the global button styles.
  > svg,
  > span {
    color: inherit !important;
  }
`

const outlinedButtonStyle = css`
  border: var(
    --secondary-button-border,
    var(--button-border, solid 1px currentColor)
  );
  background: transparent;
`
const smallButtonStyle = css`
  font-size: var(--button-font-size, ${fontSettings.small}px);
  height: var(--button-height, ${heightSettings.small}px);
  padding: 4px 22px;
`

export const IconSpan = styled.span`
  display: flex;
  align-items: center;
`

export const StyledSpan = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
`

export const LabelSpan = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap !important;
`

function getButtonStyle(props: BaseButtonProps) {
  return css`
    align-items: stretch;
    cursor: pointer;
    display: flex;
    font-family: inherit;
    font-size: var(--button-font-size, ${fontSettings.default}px);
    font-weight: var(--button-font-weight, 600);
    gap: 2px;
    height: var(--button-height, ${heightSettings.default}px);
    justify-content: center;
    max-width: 100%;
    padding: var(--button-padding, 0 22px);
    position: relative;
    text-decoration: none;
    user-select: none;

    width: ${props.block && '100%'};
    border-radius: var(
      --button-border-radius,
      ${borderRadiusSettings[props.borderStyle || 'default']}px
    );
    pointer-events: ${(props.disabled || props.isLoading) && 'none'};

    ${
      (props.disabled || props.isLoading) &&
      css`
        pointer-events: none;
        cursor: default;
      `
    }

    ${props.variant === 'outlined' ? outlinedButtonStyle : primaryButtonStyle};

    ${props.size === 'small' && smallButtonStyle};

    ${
      props.disabled &&
      css`
        filter: brightness(70%) saturate(50%);

        &:hover,
        &:focus-visible {
          filter: brightness(70%) saturate(50%);
        }
      `
    }

    &:hover,
    &:focus-visible {
      opacity: 0.9;

      // Use important to override global style on anchor tag.
      text-decoration: none !important;
    }

    &:active {
      ${!props.disabled && bounceAnimationSetting};
    }

    &::after {
      ${props.ripple && rippleRings}
      border-radius: var(
        --button-border-radius,
        ${borderRadiusSettings[props.borderStyle || 'default']}px
      );
    }
  `
}

export const StyledButton = styled.button<BaseButtonProps>`
  ${(props) => getButtonStyle(props)};
`

export const StyledAnchor = styled.a<BaseButtonProps>`
  ${(props) => getButtonStyle(props)};
`
